import React, { useEffect, useMemo, useState } from "react";
import { AiOutlineEyeInvisible, AiOutlineEye } from "react-icons/ai";
import { IoGiftOutline } from "react-icons/io5";
import { FiCheck } from "react-icons/fi";
import ClipLoader from "react-spinners/ClipLoader";
import { logo, smallLogo } from "../assets/images";
import { useUser } from "../context/user-context";
import { useNavigate, useSearchParams } from "react-router-dom";
import Register from "./Register";
import { Cookies, useCookies } from "react-cookie";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { API, IS_STAGING } from "../constants/api";
import { PRIVACY_URL, TERMS_URL } from "../constants/links";
import { errorMessage } from "../utils/errorMessage";
import { validateSignUp } from "../utils/validations";
import useAuthAxios from "../utils/baseAxios";
import { getDeviceInfo } from "../utils/deviceInfo";
import { useAuth } from "../context/auth-context";
import Oauth from "./Oauth";
declare global {
  interface Window {
    twq: (...args: any[]) => void;
  }
}
const Signup = () => {
  const { theme } = useUser();
  const { authenticate } = useAuth();
  const [email, setEmail] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const navigate = useNavigate();
  const axiosInstance = useAuthAxios();
  const [password, setPassword] = useState<string>("");
  const [viewPassword, setViewPassword] = useState<boolean>(false);
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [viewConfirmedPassword, setViewConfirmedPassword] =
    useState<boolean>(false);
  const deviceInfo = getDeviceInfo();
  const [referralCode, setReferralCode] = useState<string>("");
  const [referrer, setReferrer] = useState("");
  const [searchParams] = useSearchParams();
  const [, setCookie] = useCookies(["referral-code"]);
  useEffect(() => {
    const fromUrl = searchParams.get("r")?.trim() || "";
    const cookies = new Cookies();
    const fromCookie = cookies.get("referral-code");
    const value = fromUrl || fromCookie;
    if (value) {
      const code = String(value).toUpperCase();
      setReferralCode(code);
      setReferrer(code);
      if (fromUrl) {
        setCookie("referral-code", fromUrl, { path: "/" });
      }
    }
  }, [searchParams, setCookie]);

  const passwordChecks = useMemo(
    () => [
      { label: "At least 8 characters", met: password.length >= 8 },
      { label: "One uppercase letter", met: /[A-Z]/.test(password) },
      { label: "One lowercase letter", met: /[a-z]/.test(password) },
      { label: "One number", met: /\d/.test(password) },
      {
        label: "One special character",
        met: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
      },
    ],
    [password]
  );

  const metPasswordChecks = passwordChecks.filter((check) => check.met).length;
  const passwordStrength =
    metPasswordChecks <= 2
      ? { label: "Weak", color: "bg-red-500", width: "w-1/4" }
      : metPasswordChecks === 3
      ? { label: "Fair", color: "bg-orange-400", width: "w-2/4" }
      : metPasswordChecks === 4
      ? { label: "Good", color: "bg-yellow-400", width: "w-3/4" }
      : { label: "Strong", color: "bg-[#26A17B]", width: "w-full" };

  const showPasswordChecker = password.length > 0;
  const passwordsMatch =
    confirmPassword.length > 0 && confirmPassword === password;

  const labelClassName =
    "text-[13px] font-medium text-[#4E606E] dark:text-[#94A3B8]";
  const inputClassName =
    "h-[50px] w-full rounded-xl border border-gray-200 bg-gray-50 px-4 text-[13px] font-medium text-[#13181B] outline-none placeholder:text-gray-400 focus:border-secondary_button focus:ring-1 focus:ring-secondary_button dark:border-[#2A3441] dark:bg-[#1F2B36] dark:text-white dark:placeholder:text-[#6B7280]";

  const handleSignin = async (data: any) => {
    const response = await axiosInstance.post(API.signup, data);
    return response.data;
  };
  const completeSignin = useMutation({
    mutationFn: handleSignin,
    onSuccess: async (r) => {
      toast.success(r.message);
      // Must complete before /verify-mail: that page immediately calls the authenticated
      // endpoint api/users/me/complete-email-verification.
      await authenticate();
      //show only in production
      if (!IS_STAGING) {
        window.twq("event", "tw-pfbka-pfbkd", {
          email_address: email,
        });
      }

      setTimeout(() => {
        navigate("/verify-mail", {
          state: {
            email: email,
          },
        });
      }, 1000);
    },
    onError: (error: any) => {
      toast.error(
        errorMessage((error?.data as any)?.message || String(error?.data))
      );
    },
  });

  const handleSubmit = () => {
    if (
      !validateSignUp(firstName, lastName, email, password, confirmPassword)
    ) {
      return;
    }
    const data: { [key: string]: any } = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      password: password,
      deviceInfo: {
        name: deviceInfo.deviceName,
        uniqueId: deviceInfo.uniqueId,
        os: deviceInfo.operatingSystem?.family,
      },
    };
    if (referralCode !== "") {
      data.referrer = referralCode.trim();
    } else if (referralCode === "" && referrer) {
      data.referrer = referrer;
    }
    completeSignin.mutate(data);
  };

  return (
    <div className="w-full mds:w-4/5 md:w-3/5 xxxl:w-[50%] px-6 lgss:px-0 pt-28 pb-20 lgss:pt-16  font-manrope h-full overflow-auto mx-auto flex-col justify-between  items-center  flex">
      <div className="flex flex-col justify-center  items-center   w-full">
        <div>
          {theme === "dark" ? (
            <img src={logo} className="" alt="" />
          ) : (
            <img src={smallLogo} className="" alt="" />
          )}
        </div>
        <h2 className="text-text_black dark:text-white font-sora text-[24px] font-bold pt-4 lgss:pt-4">
          Sign up with DeeXoptions
        </h2>
        <div className="w-full mt-8 lgss:w-2/3 xxxl:w-2/5 lg:w-[60%]">
          <Oauth />
        </div>
        <form className="mt-6 pt-8 w-full px-5 lgss:px-0 lgss:w-2/3 xxxl:w-2/5 lg:w-[60%]">
          <div className="w-full flex justify-center items-center flex-col lgss:flex-row gap-4">
            <div className="w-full lgss:w-1/2">
              <label className={labelClassName}>Firstname</label>
              <input
                type="text"
                value={firstName}
                placeholder="e.g. John"
                onChange={(e) => {
                  const value = e.target.value.toLowerCase();
                  const capitalizedValue =
                    value.charAt(0).toUpperCase() + value.slice(1);
                  setFirstName(capitalizedValue);
                }}
                name="firstname"
                className={`mt-2 ${inputClassName}`}
              />
            </div>
            <div className="w-full lgss:w-1/2">
              <label className={labelClassName}>Lastname</label>
              <input
                type="text"
                value={lastName}
                placeholder="e.g. Doe"
                onChange={(e) => {
                  const value = e.target.value.toLowerCase();
                  const capitalizedValue =
                    value.charAt(0).toUpperCase() + value.slice(1);
                  setLastName(capitalizedValue);
                }}
                name="lastname"
                className={`mt-2 ${inputClassName}`}
              />
            </div>
          </div>
          <div className="mt-6">
            <label className={labelClassName}>Email</label>
            <input
              type="email"
              value={email}
              placeholder="e.g. email@domain.com"
              onChange={(e) => setEmail(e.target.value.toLowerCase())}
              name="email"
              className={`mt-2 ${inputClassName}`}
            />
          </div>
          <div className="mt-6">
            <label className={labelClassName}>Password</label>
            <div className="relative mt-2">
              <input
                type={viewPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                name="password"
                className={`${inputClassName} pr-11`}
              />
              <button
                type="button"
                onClick={() => setViewPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4E606E] dark:text-[#94A3B8]"
                aria-label={viewPassword ? "Hide password" : "Show password"}
              >
                {!viewPassword ? (
                  <AiOutlineEyeInvisible className="text-xl" />
                ) : (
                  <AiOutlineEye className="text-xl" />
                )}
              </button>
            </div>
            {showPasswordChecker && (
              <div className="mt-3 rounded-xl border border-gray-100 bg-gray-50 p-3 dark:border-[#2A3441] dark:bg-[#162033]">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[12px] font-medium text-[#4E606E] dark:text-[#9CA3AF]">
                    Password strength
                  </p>
                  <p
                    className={`text-[12px] font-semibold ${
                      passwordStrength.label === "Strong"
                        ? "text-[#26A17B]"
                        : passwordStrength.label === "Good"
                        ? "text-yellow-500"
                        : passwordStrength.label === "Fair"
                        ? "text-orange-400"
                        : "text-red-500"
                    }`}
                  >
                    {passwordStrength.label}
                  </p>
                </div>
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-[#1F2937]">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${passwordStrength.color} ${passwordStrength.width}`}
                  />
                </div>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {passwordChecks.map((check) => (
                    <div
                      key={check.label}
                      className="flex items-center gap-2 text-[12px]"
                    >
                      <span
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${
                          check.met
                            ? "bg-[#26A17B] text-white"
                            : "bg-gray-200 text-transparent dark:bg-[#1F2937]"
                        }`}
                      >
                        <FiCheck className="text-[10px]" />
                      </span>
                      <span
                        className={
                          check.met
                            ? "text-[#13181B] dark:text-white"
                            : "text-[#4E606E] dark:text-[#9CA3AF]"
                        }
                      >
                        {check.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="mt-6">
            <label className={labelClassName}>Confirm Password</label>
            <div className="relative mt-2">
              <input
                type={viewConfirmedPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                name="confirm password"
                className={`${inputClassName} pr-11`}
              />
              <button
                type="button"
                onClick={() => setViewConfirmedPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4E606E] dark:text-[#94A3B8]"
                aria-label={
                  viewConfirmedPassword
                    ? "Hide confirm password"
                    : "Show confirm password"
                }
              >
                {!viewConfirmedPassword ? (
                  <AiOutlineEyeInvisible className="text-xl" />
                ) : (
                  <AiOutlineEye className="text-xl" />
                )}
              </button>
            </div>
            {confirmPassword.length > 0 && (
              <p
                className={`mt-2 text-[12px] font-medium ${
                  passwordsMatch ? "text-[#26A17B]" : "text-red-500"
                }`}
              >
                {passwordsMatch
                  ? "Passwords match"
                  : "Passwords do not match"}
              </p>
            )}
          </div>
          <div className="mt-6">
            <div className="flex items-center gap-2">
              <IoGiftOutline className="text-[16px] text-secondary_button" />
              <label className={labelClassName}>
                Invite code / Referral code (optional)
              </label>
            </div>
            <input
              type="text"
              value={referralCode}
              placeholder="E.G. DX-WELCOME500"
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              className={`mt-2 ${inputClassName} uppercase tracking-wide`}
            />
          </div>
        </form>
      </div>
      <div className="w-full mds:w-[90%] lgss:w-2/3 xxxl:w-2/5 lg:w-[60%] mt-8 flex flex-col justify-center items-center">
        <div className="font-medium text-center text-[#617889] text-[12px]">
          By clicking “Sign up” you have read our{" "}
          <a
            href={PRIVACY_URL}
            target="_blank"
            rel="noreferrer"
            className="font-bold text-secondary_button"
          >
            Privacy notice{" "}
          </a>{" "}
          accept our{" "}
          <a
            href={TERMS_URL}
            target="_blank"
            rel="noreferrer"
            className="font-bold text-secondary_button"
          >
            Terms and conditions
          </a>{" "}
          and you agree to it.
        </div>
        <button
          onClick={handleSubmit}
          disabled={completeSignin.isPending}
          className="w-full  h-[52px] flex justify-center items-center font-manrope font-medium rounded-xl text-white bg-secondary_button mt-4 lgss:mt-8"
        >
          {completeSignin.isPending ? (
            <ClipLoader color="#FFFFFF" size={30} />
          ) : (
            "Sign Up"
          )}
        </button>
        <h4 className="font-manrope dark:text-gray-300  text-text_black font-medium text-[12px] pt-6">
          Already have an account?{" "}
          <span
            onClick={() => {
              navigate("/login");
            }}
            className="underline text-secondary_button tracking-wider cursor-pointer"
          >
            Login
          </span>
        </h4>
      </div>
    </div>
  );
};

const SignupPage = () => (
  <div className="lgss:flex lgss:flex-row h-screen dark:bg-[#13181B]">
    <Register />
    <Signup />
  </div>
);

export default SignupPage;
